'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();

import { Skeleton } from '@workspace/ui/components/skeleton';
import { cn } from '@workspace/ui/lib';
import { useEffect, useMemo, useState } from 'react';

interface G2ReviewIframeProps {
  reviewUrl: string;
  onComplete?: () => void;
}

export function G2ReviewIframe({ reviewUrl, onComplete }: G2ReviewIframeProps) {
  const [isLoading, setIsLoading] = useState(true);

  // Derive allowed origin from the review URL for security
  const allowedOrigin = useMemo(() => {
    try {
      return new URL(reviewUrl).origin;
    } catch {
      return null;
    }
  }, [reviewUrl]);

  useEffect(() => {
    // Listen for messages from the G2 iframe to detect completion
    const handleMessage = (event: MessageEvent) => {
      // Only accept messages from the allowed origin
      if (allowedOrigin && event.origin !== allowedOrigin) {
        return;
      }

      // G2 sends a postMessage when the review is submitted
      if (event.data?.type === 'g2-review-submitted' || event.data === 'g2-review-submitted') {
        onComplete?.();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [onComplete, allowedOrigin]);

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex flex-col gap-2">
        <h3 className="text-xl font-bold text-neutral-200 dark:text-neutral-100">
          Share your experience on G2
        </h3>
        <p className="text-grey-600 dark:text-grey-400 text-sm">
          Your review helps others discover our platform. It only takes a few minutes.
        </p>
      </div>

      <div className="relative w-full overflow-hidden rounded-2xl border border-grey-300 dark:border-grey-600">
        {isLoading && (
          <div className="flex h-[500px] w-full flex-col gap-6 bg-neutral-100 p-6 dark:bg-grey-1000">
            <div className="flex flex-col gap-2">
              <Skeleton className="bg-grey-200 dark:bg-grey-800 h-6 w-2/3" />
              <Skeleton className="bg-grey-200 dark:bg-grey-800 h-4 w-full" />
              <Skeleton className="bg-grey-200 dark:bg-grey-800 h-4 w-4/5" />
            </div>
            <div className="flex flex-1 flex-col gap-4">
              <Skeleton className="bg-grey-200 dark:bg-grey-800 h-10 w-full rounded-md" />
              <Skeleton className="bg-grey-200 dark:bg-grey-800 h-10 w-full rounded-md" />
              <Skeleton className="bg-grey-200 dark:bg-grey-800 h-24 w-full rounded-md" />
              <Skeleton className="bg-grey-200 dark:bg-grey-800 h-10 w-1/3 rounded-md" />
            </div>
          </div>
        )}

        <iframe
          src={reviewUrl}
          title="G2 Review Form"
          className={cn('w-full border-0', isLoading ? 'h-0' : 'h-[500px]')}
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-top-navigation"
          onLoad={() => setIsLoading(false)}
        />
      </div>

      <button
        type="button"
        onClick={onComplete}
        className={cn(
          'inline-flex h-12 items-center justify-center rounded-[60px] border px-8 text-base font-medium transition-all',
          'border-primary text-primary dark:border-neutral-100 dark:text-neutral-100',
          'hover:bg-grey-50 dark:hover:bg-grey-200 dark:hover:text-primary'
        )}
      >
        I&apos;ve finished my review
      </button>
    </div>
  );
}
